import React from "react";
import { Box, Typography } from "@mui/material";
import { ChevronRightIcon } from "screens/SignRequest/components/EditorIcons";
import tokens from "screens/SignRequest/SignRequestEditor.tokens";
import { tourAttr } from "tour";
import { inkfree } from "theme/colors";

interface Step {
  label: string;
  isActive: boolean;
  isCompleted: boolean;
}

interface CustomStepperProps {
  steps: Step[];
  currentStep: number;
  handleChangeStep: Function;
}

const { stepper } = tokens;

const CustomStepper: React.FC<CustomStepperProps> = ({
  steps,
  currentStep,
  handleChangeStep,
}) => {
  return (
    <Box
      {...tourAttr("request.stepper")}
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignSelf: "stretch",
        alignItems: "center",
        justifyContent: "center",
        gap: stepper.gap,
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {steps.map((step, index) => {
        // A step behind the cursor is navigable; the one ahead of the current
        // step is reachable too (the design lets you jump forward one).
        const isDone = index < currentStep;
        const isNext = index === currentStep + 1;
        const isNavigable = isDone || isNext;

        return (
          <Box
            key={step.label}
            sx={{ display: "flex", alignItems: "center", gap: stepper.gap }}
          >
            <Box
              component="span"
              role={isNavigable ? "button" : undefined}
              tabIndex={isNavigable ? 0 : -1}
              aria-label={isNavigable ? `Go to step ${step.label}` : undefined}
              onClick={() => isNavigable && handleChangeStep(index)}
              onKeyDown={
                isNavigable
                  ? (e: React.KeyboardEvent) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleChangeStep(index);
                      }
                    }
                  : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: stepper.gap,
                cursor: isNavigable ? "pointer" : "default",
                "&:focus-visible": {
                  outline: `2px solid ${inkfree.main}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: stepper.dotSize,
                  height: stepper.dotSize,
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor:
                    step.isActive || isDone ? stepper.dotDone : stepper.dotTodo,
                }}
              />
              <Typography
                component="span"
                sx={{
                  fontSize: stepper.fontSize,
                  fontWeight: step.isActive
                    ? stepper.weightCurrent
                    : stepper.weightOther,
                  color: step.isActive
                    ? stepper.colorCurrent
                    : isDone
                      ? stepper.colorDone
                      : stepper.colorTodo,
                  whiteSpace: "nowrap",
                  lineHeight: "normal",
                }}
              >
                {step.label}
              </Typography>
            </Box>

            {index < steps.length - 1 && (
              <Box
                component="span"
                sx={{
                  display: "flex",
                  mx: stepper.arrowMarginX,
                  color: stepper.arrowColor,
                }}
              >
                <ChevronRightIcon size={stepper.arrowSize} />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default CustomStepper;
